<script setup lang="ts">
import { sleep } from "@voltalis/frontend-utils";
import { ref, watch } from "vue";

interface IProps {
  beforeClose?: () => boolean;
  headerClass?: string;
}

interface IEmits {
  (e: "hide"): void;
}

const visible = defineModel<boolean>({
  required: true,
});
const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();
defineSlots<{
  title: () => unknown;
  default: () => unknown;
}>();

const isSidebarVisible = ref<boolean>(false);

async function handleClose() {
  if (props.beforeClose === undefined) {
    hide();
    return;
  }

  const canClose = props.beforeClose();

  if (!canClose) return;
  hide();
}

async function hide() {
  await slideOut();
  emits("hide");
  visible.value = false;
}

function handleDrawerClick(event: Event) {
  event.stopPropagation();
  event.stopImmediatePropagation();
  event.preventDefault();
}

async function slideIn() {
  await sleep(10);
  isSidebarVisible.value = true;
}

async function slideOut() {
  await sleep(10);
  isSidebarVisible.value = false;
  await sleep(200);
}

watch(visible, async (newValue) => {
  if (newValue) {
    await slideIn();
  }
});

defineExpose<{
  handleClose: () => Promise<void>;
}>({
  handleClose,
});
</script>

<template>
  <div
    v-if="visible"
    class="base-drawer-overlay"
    @click="handleClose"
  >
    <div class="base-drawer-overlay-content">
      <div class="base-drawer-positioner">
        <div class="base-drawer-spacing">
          <div
            :class="['base-drawer-sidebar', { visible: isSidebarVisible }]"
            @click="handleDrawerClick($event)"
          >
            <div :class="['base-drawer-header', props.headerClass]">
              <div class="base-drawer-title">
                <slot name="title" />
              </div>
              <button @click="handleClose">
                <i class="pi pi-times" />
              </button>
            </div>

            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-drawer-overlay {
  @apply fixed inset-0 w-full h-full;

  z-index: 900;
  backdrop-filter: blur(4px);
}

.base-drawer-overlay-content {
  @apply relative w-full h-full;
}

.base-drawer-positioner {
  @apply absolute right-0 h-full;
}

.base-drawer-spacing {
  @apply h-full py-4 pr-4;
}

.base-drawer-sidebar {
  @apply flex flex-col w-[520px] h-full bg-white rounded-lg shadow-lg translate-x-full transition-all duration-200 overflow-clip;

  &.visible {
    @apply translate-x-0;
  }
}

.base-drawer-header {
  @apply flex items-center justify-end p-6 border-b;
}

.base-drawer-title {
  @apply flex flex-1;
}
</style>
